import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/shared/api';

import type { TypeEngineAgent, TypeEngineAgentUpdate } from '@intone/shared/types';

export interface AgentListItem {
  alt_key: string
  name: string
  campaign_id: number
  campaign_name: string
  campaign_telephony: string
  llm_name: string
  voice_name: string
  stt_config_id: number
  stt_config_copied: boolean
  turn_detection_config_id: number
  turn_detection_config_copied: boolean
  timeout_config_id: number
  timeout_config_copied: boolean
}

export function useEngineAgentsList() {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['engineAgents'],
    queryFn: async () => {
      return apiClient<AgentListItem[]>('/engine-agents');
    },
  });
}

export function useEngineAgent(agentId: string | undefined) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['engineAgent', agentId],
    queryFn: async () => {
      return apiClient<TypeEngineAgent>(`/engine-agents/${agentId}`);
    },
  });
}

/**
 * Mutation hook to update an existing engine agent.
 * Invalidates the relevant `engineAgent` query on success so the UI stays in sync.
 *
 * @returns A React Query mutation whose `mutate`/`mutateAsync` accepts
 *          `{ agentId: string } & EngineAgentUpdate`.
 *
 * @example
 * ```ts
 * const { mutateAsync: updateAgent } = useUpdateEngineAgent();
 * await updateAgent({ agentId: 'abc-123', name: 'New Name', llm_id: 1, ... });
 * ```
 */
export function useCreateEngineAgent() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (campaignId: number) => {
      return apiClient<{ alt_key: string; message: string }>(
        '/engine-agents',
        { method: 'POST', body: JSON.stringify({ campaign_id: campaignId }) }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['engineAgents'] });
    },
  });
}

export function useDuplicateEngineAgent() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (altKey: string) => {
      return apiClient<{ alt_key: string; message: string }>(
        `/engine-agents/${altKey}/duplicate`,
        { method: 'POST' }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['engineAgents'] });
    },
  });
}

export function useDeleteEngineAgent() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (altKey: string) => {
      return apiClient<{ success: boolean; message: string }>(
        `/engine-agents/${altKey}`,
        { method: 'DELETE' }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['engineAgents'] });
    },
  });
}

export function useUpdateEngineAgent() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ agentId, ...data }: { agentId: string } & TypeEngineAgentUpdate) => {
      return apiClient<TypeEngineAgent>(`/engine-agents/${agentId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['engineAgents'] });
      queryClient.invalidateQueries({ queryKey: ['engineAgent', variables.agentId] });
    },
  });
}